import React, { useEffect, useState } from "react"
import { useResultsTable } from "@/features/accounts/ocr/hooks/use-results-table";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { TmpTrades } from "@/features/accounts/ocr/hooks/use-results-table-states";
import { AmountForm } from "@/shared/components/text-box/amount-form";
import { DateForm } from "@/shared/components/text-box/date-form";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { Checkbox } from "@/shared/components/check-box/check-box";
import { Reorder, useDragControls } from "framer-motion";
import { DuplicateTradesModal } from "@/features/accounts/shared/components/duplicate-trades-modal";
import { OverlapInfo } from "@/features/accounts/ocr/services/post-trades-model";
import { useInputValidation } from "@/shared/hooks/use-input-validation";

interface Props {
  imageId: string  // 選択されているファイル名(内部ID)
  imageOriginName: string  // 選択されている
  onProcessedComplete: (imageId: string) => void
  isCompleted: boolean  // 画像の処理完了フラグ
}

interface DraggableRowProps {
  trade: TmpTrades;
  isCompleted: boolean;
  selectedTradeIds: string[];
  handleUpdateTmpTrade: (tradeId: string, field: keyof TmpTrades, value: string) => void;
  toggleTrade: (tradeId: string) => void;
}

const DraggableRow = ({ trade, isCompleted, selectedTradeIds, handleUpdateTmpTrade, toggleTrade }: DraggableRowProps) => {
  const dragControls = useDragControls();
  
  const [isComposing, setIsComposing] = useState(false);
  const { validate } = useInputValidation("deleteOver4Bytes");

  const handleTextChange = (field: "summary" | "remark", value: string, composing = isComposing) => {
    let formatValue = value;
    // 全角記号を半角に変換
    formatValue = validate(formatValue, composing);
    handleUpdateTmpTrade(trade.tradeId, field, formatValue);
  };

  return (
    <Reorder.Item
      value={trade}
      as="tr"
      id={trade.tradeId}
      dragListener={false}
      dragControls={dragControls}
      style={{ position: "relative" }} // 必要に応じてlayoutのため
    >
      <td className="col-drag">
        <div
          onPointerDown={(e) => !isCompleted && dragControls.start(e)}
          style={{ touchAction: "none" }}
        >
          {/* ドラッグハンドルアイコン（6点グリップ） */}
          <span className="drag-icon"></span>
        </div>
      </td>
      <td>
        <DateForm
          onChange={(val) => handleUpdateTmpTrade(trade.tradeId, "date", val)}
          initialValue={trade.date}
          isDisabled={isCompleted}
        />
      </td>
      <td>
        <input
          type="text"
          className="main"
          onChange={(e) => handleTextChange("summary", e.target.value)}
          value={validate(trade.summary, isComposing).slice(0, 100)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            handleTextChange("summary", e.currentTarget.value, false);
          }}
          maxLength={100}
        />
        <input
          type="text"
          className="sub"
          onChange={(e) => handleTextChange("remark", e.target.value)}
          value={validate(trade.remark, isComposing).slice(0, 100)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            handleTextChange("remark", e.currentTarget.value, false);
          }}
          maxLength={100}
        />
      </td>
      <td>
        <AmountForm
          onChange={(val) => handleUpdateTmpTrade(trade.tradeId, "withdrawal", val)}
          initialValue={trade.withdrawal}
          isDisabled={isCompleted}
        />
      </td>
      <td>
        <AmountForm
          onChange={(val) => handleUpdateTmpTrade(trade.tradeId, "deposit", val)}
          initialValue={trade.deposit}
          isDisabled={isCompleted}
        />
      </td>
      <td>
        <AmountForm
          onChange={(val) => handleUpdateTmpTrade(trade.tradeId, "balance", val)}
          initialValue={trade.balance}
          isDisabled={isCompleted}
        />
      </td>
      <td className="check_del">
        <Checkbox
          label=""
          checked={selectedTradeIds.includes(trade.tradeId)}
          handleChange={() => toggleTrade(trade.tradeId)}
          isDisabled={isCompleted}
        />
      </td>
    </Reorder.Item>
  );
};

export const OcrResultsTable = (props: Props) => {
  const {
    imageId,
    imageOriginName,
    onProcessedComplete,
    isCompleted,
  } = props

  const {
    buildDraftKey,
    getTmpTradesByImage,
    updateTmpTradesByImage,
    setTmpTrades,
    confirmedTrades,
  } = useResultsTable();

    const {
    selectedAccountGroup,
    selectedAccount,
  } = useAccountGroupsContext();

  const [isLoading, setIsLoading] = useState(true)
  const [tradesForImage, setTradesForImage] = useState<TmpTrades[]>([])
  const [isFetched, setIsFetched] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [overlapInfo, setOverlapInfo] = useState<OverlapInfo>();
  
  const { validate } = useInputValidation("deleteOver4Bytes");

  // 初期表示の際はAPI経由でOCR結果を取得・保存し、localStorageから取得
  useEffect(() => {
    const fetchOcrResults = async () => {
      setIsLoading(true)
      try {
        if (selectedAccount) {
          if (!isFetched) {
            await setTmpTrades(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId)
            setIsFetched(true)
          }
          // localStorageから取得
          const byImage = getTmpTradesByImage(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId)
          setTradesForImage(byImage[imageId] ?? [])
        } else {
          setTradesForImage([])
        }
      } catch (e) {
        // エラー時はページを閉じる
        console.log("fetchOcrResults error:", e);
        onProcessedComplete("");
        return;
      } finally {
        setIsLoading(false);
      }
    };
    fetchOcrResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId, selectedAccount, selectedAccountGroup])

  // 編集・追加・削除時はlocalStorageから再取得
  const refreshTradesForImage = () => {
    if (!selectedAccount) return;
    const byImage = getTmpTradesByImage(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId)
    setTradesForImage(byImage[imageId] ?? [])
  }

  // ドラッグ＆ドロップでの並び替え
  const handleReorder = (newOrder: TmpTrades[]) => {
    if (isCompleted) return;
    
    // UI更新
    setTradesForImage(newOrder);

    // データ永続化
    if (!selectedAccount) {
      return;
    }
    const draftKey = buildDraftKey(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId);
    
    // updateTmpTradesByImage は updater 関数を受け取るが、ここでは直接上書きしたい
    // なので、特定の imageId の配列だけ newOrder に置き換える
    updateTmpTradesByImage((prev) => ({
      ...prev,
      [imageId]: newOrder,
    }), draftKey);
  };

  const [selectedTradeIds, setSelectedTradeIds] = useState<string[]>([]);  // チェックボックス用選択された取引IDの配列

  // チェックボックス制御ロジック
  const toggleTrade = (tradeId: string) => {
    setSelectedTradeIds(prev => prev.includes(tradeId) ? prev.filter(id => id !== tradeId) : [...prev, tradeId]);
  }

  // 全選択用
  const toggleAllTrades = () => {
    if (selectedTradeIds.length === tradesForImage.length) {
      setSelectedTradeIds([]);
    } else {
      setSelectedTradeIds(tradesForImage.map(trade => trade.tradeId));
    }
  }

  // 行追加ボタン押下時処理
  const handleAddRow = () => {
    if (isCompleted) {
      return;
    } 
    if (!selectedAccount) {
      return;
    }
    const newTrade: TmpTrades = {
      ocrAccountId: selectedAccount.mlinkAccountId,
      ocrProcessId: selectedAccount.ocrProcessId,
      tradeId: crypto.randomUUID().replace(/-/g, ""),
      fileName: imageId,
      date: "",
      sortOrder: tradesForImage.length + 1,
      summary: "",
      withdrawal: "",
      deposit: "",
      balance: "",
      remark: "",
    }
    updateTmpTradesByImage((prev) => ({
      ...prev,
      [imageId]: [...(prev[imageId] || []), newTrade],
    }), buildDraftKey(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId));
    refreshTradesForImage();
  }

  // 削除処理
  const handleDeleteSelected = () => {
    if (isCompleted) {
      return
    }
    if (selectedTradeIds.length === 0) {
      return
    }
    if (!selectedAccount) {
      return;
    }
    updateTmpTradesByImage((prev) => {
      const current = prev[imageId] || [];
      return {
        ...prev,
        [imageId]: current.filter((trade) => !selectedTradeIds.includes(trade.tradeId)),
      };
    }, buildDraftKey(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId));
    setSelectedTradeIds([]); // 選択状態をリセット
    refreshTradesForImage();
  }

  // 明細編集時処理
  const handleUpdateTmpTrade = (tradeId: string, field: keyof TmpTrades, value: string) => {
    if (isCompleted) {
      return
    }
    if (!selectedAccount) {
      return;
    }
    updateTmpTradesByImage((prev) => ({
      ...prev,
      [imageId]: prev[imageId].map((trade) =>
        trade.tradeId === tradeId ? { ...trade, [field]: value } : trade
      ),
    }), buildDraftKey(selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId));
    refreshTradesForImage();
  };

  // 明細確定ボタン押下時処理
  const handleSubmitClick = async () => {
    if (isCompleted) {
      return;
    }
    if (tradesForImage.length === 0) {
      return;
    }

    // 日付バリデーション
    const emptyDateRow = tradesForImage.find(trade => !trade.date || trade.date.trim() === "");
    if (emptyDateRow) {
      alert("日付を入力してください");
      return;
    }

    // 入金・出金バリデーション
    const invalidRow = tradesForImage.find(trade => {
      const depositFilled = trade.deposit.trim() !== "";
      const withdrawalFilled = trade.withdrawal.trim() !== "";
      return (depositFilled && withdrawalFilled) || (!depositFilled && !withdrawalFilled);
    });
    if (invalidRow) {
      alert("入金または出金のどちらか一方のみ入力してください");
      return;
    }

    const tradeList = tradesForImage.map((trade, index) => {
      const isDeposit = trade.deposit.trim() !== "";
      // 送信前に両端の空白を削除
      const summary = validate(trade.summary, false).slice(0, 100).trim();
      const remark = validate(trade.remark, false).slice(0, 100).trim();
      return {
        trade_id: trade.tradeId,
        date: trade.date,
        ml_sort_order: index + 1,
        trade_type: isDeposit ? "1" : "2",
        amount: isDeposit ? trade.deposit : trade.withdrawal,
        balance: trade.balance ? trade.balance : "0",
        summary: summary,
        remark: remark,
      };
    });

    if (!selectedAccount) {
      return;
    }
    const res = await confirmedTrades(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId, imageId, tradeList);
    if (res && res.overlap_info) {
      setOverlapInfo(res.overlap_info);
      setShowImportDialog(true);
      return;
    }

    // 処理完了コールバック
    onProcessedComplete(imageId);
  }

  const handleImportMethodSelect = async (method: string) => {
    if (!selectedAccount) return;
    
    const tradeList = tradesForImage.map((trade, index) => {
      const isDeposit = trade.deposit.trim() !== "";
      return {
        trade_id: trade.tradeId,
        date: trade.date,
        ml_sort_order: index + 1,
        trade_type: isDeposit ? "1" : "2",
        amount: isDeposit ? trade.deposit : trade.withdrawal,
        balance: trade.balance ? trade.balance : "0",
        summary: validate(trade.summary, false).slice(0, 100).trim(),
        remark: validate(trade.remark, false).slice(0, 100).trim(),
      };
    });

    await confirmedTrades(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId, imageId, tradeList, method);
    setShowImportDialog(false);
    onProcessedComplete(imageId);
  };


  if (isLoading) {
    return (
      <div className="w-1/2 bg-background rounded-lg shadow-xl flex items-center justify-center">
        <div className="text-muted-foreground">OCR結果を読み込み中...</div>
      </div>
    )
  }

  return (
    <section id="ocrEditBox">
      <div className="table-wrapper">
        <input type="checkbox" id="modeEdit0001" name="modeEdit0001" className="edit" />
        <table id={imageId} className="active">
          <caption>
            <strong>{imageOriginName}の読み取り結果</strong>
            <label htmlFor="modeEdit0001" className="trg_off">キャンセル</label>
            <label htmlFor="modeEdit0001" className="trg_on">選択</label>
            {/* 下記ボタンは「選択」押下すると出てくるゴミ箱ボタン */}
            <BaseRoundButton
              className="trg_del"
              title="チェックした行を削除"
              onClick={handleDeleteSelected}
              isActive={!isCompleted && selectedTradeIds.length > 0}
              loadingNegative={true}
            />
          </caption>
          <thead>
            <tr>
              <th className="col-drag"></th>
              <th scope="col">年月日</th>
              <th scope="col">取引内容(備考)</th>
              <th scope="col">出金</th>
              <th scope="col">入金</th>
              <th scope="col">残高</th>
              <th className="check_del">
                <Checkbox
                  label=""
                  checked={false}
                  className="check_row_all"
                  id="selectRowAll"
                  handleChange={toggleAllTrades}
                />
              </th>
            </tr>
          </thead>
          <Reorder.Group
            as="tbody"  // tbody要素として扱わせる
            axis="y"  // 並び替えの方向(本ページでは縦なのでy)
            values={tradesForImage}
            onReorder={handleReorder}
            key={imageId}
          >
            {tradesForImage.map((trades) => (
              <DraggableRow
                key={trades.tradeId}
                trade={trades}
                isCompleted={isCompleted}
                selectedTradeIds={selectedTradeIds}
                handleUpdateTmpTrade={handleUpdateTmpTrade}
                toggleTrade={toggleTrade}
              />
            ))}
          </Reorder.Group>
        </table>
      </div>

      {/* フッター */}
      <div className="item-wrapper">
        <BaseRoundButton
          buttonText="行を追加する"
          id="addTr"
          onClick={handleAddRow}
          isActive={isCompleted? false : true}
          loadingNegative={true}
        />
        <BaseRoundButton
          buttonText="この内容を明細に追加"
          id="uploadOcr"
          onClick={handleSubmitClick}
          isActive={isCompleted? false : true}
          loadingNegative={true}
        />
      </div>
      {showImportDialog && overlapInfo && (
        <DuplicateTradesModal
          onClose={() => setShowImportDialog(false)}
          onSelectMethod={handleImportMethodSelect}
          overlapInfo={overlapInfo}
          isSub={true}
        />
      )}
    </section>
  )
}



